"use client";

import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar as ChartBar } from "react-chartjs-2";
import {
  CheckCircle,
  TrendingUp,
  TrendingDown,
  BadgeEuro,
  Settings,
  Calculator,
  Link2,
  RotateCcw,
} from "lucide-react";
import SliderInputs from "@components/features/configurator/SliderInputs";
import { ConfiguratorInput } from "@components/features/configurator/types";
import { useUrlConfig } from "@components/features/configurator/useUrlConfig";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);
ChartJS.unregister(ChartDataLabels);

export interface ROICalculatorConfig {
  seoChip: string;
  titleLine1: string;
  titleLine2: string;
  description: string;
  inputs: Array<{ id: string; label: string; min: number; max: number; default: number; step: number }>;
  formula: { errorRateCostPercent: number; redundancyFactor: number; powerBiRunningCostBase: number; powerBiRunningCostPerPage: number };
}

const DEFAULT_ROI_INPUTS: ConfiguratorInput[] = [
  {
    id: "reportPages",
    label: "Anzahl Reportseiten",
    min: 10,
    max: 200,
    default: 25,
    step: 5,
  },
  {
    id: "hoursPerMonth",
    label: "Pflegeaufwand je Seite/Monat",
    min: 2,
    max: 40,
    default: 10,
    step: 1,
    unit: "h",
  },
  {
    id: "hourlyRate",
    label: "Interner Stundensatz",
    min: 30,
    max: 200,
    default: 40,
    step: 5,
    unit: "€",
  },
];

const DEFAULT_ROI_FORMULA = {
  errorRateCostPercent: 21,
  redundancyFactor: 1.05,
  powerBiRunningCostBase: 2500,
  powerBiRunningCostPerPage: 634,
};

export function useROICalculator(config: ROICalculatorConfig | undefined) {
  const inputs: ConfiguratorInput[] = config ? config.inputs : DEFAULT_ROI_INPUTS;
  const { values, setValue, resetValues, appliedFromUrl, buildUrl } = useUrlConfig(inputs);

  React.useEffect(() => {
    if (!appliedFromUrl) {
      return;
    }

    requestAnimationFrame(() => {
      const el = document.getElementById("roi-calculator");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, [appliedFromUrl]);

  const reportPages = values.reportPages;
  const hoursPerMonth = values.hoursPerMonth;
  const hourlyRate = values.hourlyRate;

  if (
    typeof reportPages !== "number" ||
    typeof hoursPerMonth !== "number" ||
    typeof hourlyRate !== "number"
  ) {
    throw new Error(
      "useROICalculator: expected reportPages, hoursPerMonth and hourlyRate values."
    );
  }

  const setReportPages = React.useCallback((next: number) => setValue("reportPages", next), [setValue]);
  const setHoursPerMonth = React.useCallback((next: number) => setValue("hoursPerMonth", next), [setValue]);
  const setHourlyRate = React.useCallback((next: number) => setValue("hourlyRate", next), [setValue]);

  const formula = config?.formula ?? DEFAULT_ROI_FORMULA;

  const computed = React.useMemo(() => {
    const manualCost = reportPages * hoursPerMonth * hourlyRate * 12;
    const errorCost = manualCost * (formula.errorRateCostPercent / 100);
    const totalExcelCost = Math.round((manualCost + errorCost) * formula.redundancyFactor);
    const powerBiCost = formula.powerBiRunningCostBase + reportPages * formula.powerBiRunningCostPerPage;
    const annualSaving = totalExcelCost - powerBiCost;
    return { totalExcelCost, powerBiCost, annualSaving };
  }, [reportPages, hoursPerMonth, hourlyRate, formula.errorRateCostPercent, formula.redundancyFactor, formula.powerBiRunningCostBase, formula.powerBiRunningCostPerPage]);

  const getShareUrl = React.useCallback(() => {
    if (typeof window === "undefined") {
      throw new Error("useROICalculator: getShareUrl is only available in browser.");
    }
    return buildUrl(window.location.pathname, {}, "roi-calculator");
  }, [buildUrl]);

  return {
    reportPages, setReportPages,
    hoursPerMonth, setHoursPerMonth,
    hourlyRate, setHourlyRate,
    resetValues,
    appliedFromUrl,
    getShareUrl,
    ...computed,
  };
}

const roiFmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const MigrationROICalculator = React.memo(function MigrationROICalculator({ config, calc }: {
  config: ROICalculatorConfig;
  calc: ReturnType<typeof useROICalculator>;
}) {
  const [copied, setCopied] = React.useState(false);
  const pageInput = config.inputs.find(i => i.id === "reportPages");
  const hoursInput = config.inputs.find(i => i.id === "hoursPerMonth");
  const rateInput = config.inputs.find(i => i.id === "hourlyRate");

  if (!pageInput || !hoursInput || !rateInput) {
    throw new Error(
      "MigrationROICalculator: missing reportPages, hoursPerMonth or hourlyRate input."
    );
  }

  const sliderInputs: ConfiguratorInput[] = [
    pageInput,
    { ...hoursInput, unit: "h" },
    { ...rateInput, unit: "€" },
  ];

  const handleReset = React.useCallback(() => {
    calc.resetValues();
  }, [calc]);

  const { getShareUrl } = calc;
  const handleShare = React.useCallback(() => {
    const url = getShareUrl();
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [getShareUrl]);

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 shadow-xl p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="flex items-center gap-2 text-lg font-bold text-abis-primary leading-none">
          <Calculator className="w-5 h-5 text-abis-primary flex-shrink-0" />
          <span className="text-xs font-semibold text-abis-accent uppercase tracking-wide">{config.seoChip}</span>
          <span className="mx-1 text-gray-300">|</span>
          {config.titleLine1} <span className="text-abis-accent">{config.titleLine2}</span>
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all duration-200 bg-gray-50 text-gray-500 border-gray-200 hover:bg-abis-primary/5 hover:text-abis-primary hover:border-abis-primary/20"
            title="Standardwerte wiederherstellen"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Zurücksetzen
          </button>
          <button
            onClick={handleShare}
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all duration-200 ${
              copied
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-abis-primary/5 hover:text-abis-primary hover:border-abis-primary/20"
            }`}
            title="Link mit aktuellen Werten kopieren"
          >
            {copied ? <CheckCircle className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            {copied ? "Kopiert!" : "Teilen"}
          </button>
        </div>
      </div>
      <SliderInputs
        inputs={sliderInputs}
        values={{
          reportPages: calc.reportPages,
          hoursPerMonth: calc.hoursPerMonth,
          hourlyRate: calc.hourlyRate,
        }}
        onValueChange={(id, value) => {
          if (id === "reportPages") {
            calc.setReportPages(value);
            return;
          }
          if (id === "hoursPerMonth") {
            calc.setHoursPerMonth(value);
            return;
          }
          if (id === "hourlyRate") {
            calc.setHourlyRate(value);
            return;
          }
          throw new Error(`MigrationROICalculator: unsupported input id "${id}".`);
        }}
        className="mb-8"
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-red-50 rounded-xl p-4 border border-red-100">
          <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">Jährliche Excel-Kosten</p>
          <p className="text-2xl font-bold text-red-700 tabular-nums">{roiFmt.format(calc.totalExcelCost)}</p>
          <p className="text-xs text-gray-500 mt-1">inkl. Fehler &amp; Redundanzen</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Power BI Betrieb/Jahr</p>
          <p className="text-2xl font-bold text-blue-700 tabular-nums">{roiFmt.format(calc.powerBiCost)}</p>
          <p className="text-xs text-gray-500 mt-1">Lizenzen &amp; Administration</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-100 ring-2 ring-green-200">
          <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Ihre jährliche Einsparung</p>
          <p className="text-2xl font-bold text-green-700 tabular-nums">{roiFmt.format(calc.annualSaving)}</p>
          <p className="text-xs text-gray-500 mt-1">ab Jahr 2 nach Migration</p>
        </div>
      </div>
    </div>
  );
});

export interface ROIAnalysisSectionProps {
  roiAnalysis: {
    subtitle: string;
    description?: string;
    chartTitle?: string;
    costComparison?: { category: string; detail?: string }[];
    highlights?: { label: string; icon?: string }[];
    roiTimeline?: { year: string; description: string }[];
    tableHeaders?: { category?: string; excelCost?: string; powerbiCost?: string; savings?: string; total?: string };
    sources?: string[];
    footnote?: string;
    migrationScope?: {
      title: string;
      subtitle: string;
      totalEffort: string;
      investmentYear1: number;
      baseReportPages: number;
      basePersonDays: number;
      costPerPersonDay: number;
      scalingPhases: string[];
      fixedPhases: string[];
      uspText: string;
      subtitleTemplate: string;
      effortLabel: string;
      investmentLabel: string;
      phases: { phase: string; title: string; percentage: number }[];
      deliverables: { label: string; value: string }[];
    };
  };
  roiCalculatorConfig: ROICalculatorConfig | undefined;
  roiCalc: ReturnType<typeof useROICalculator>;
  reportPages: number;
  hoursPerMonth: number;
  hourlyRate: number;
}

const fmtEuro = (n: number) => n.toLocaleString("de-DE");
const formatEuro = (value: number) => `${value >= 0 ? "+" : ""}${value.toLocaleString("de-DE")} €`;

export default function ROIAnalysisSection({
  roiAnalysis,
  roiCalculatorConfig,
  roiCalc,
  reportPages: rp,
  hoursPerMonth: hpm,
  hourlyRate: hr,
}: ROIAnalysisSectionProps) {
  const formula = roiCalculatorConfig?.formula ?? { errorRateCostPercent: 21, redundancyFactor: 1.05, powerBiRunningCostBase: 2500, powerBiRunningCostPerPage: 634 };
  const manualCostDyn = rp * hpm * hr * 12;
  const errorCostDyn = manualCostDyn * (formula.errorRateCostPercent / 100);
  const dynExcelCost = Math.round((manualCostDyn + errorCostDyn) * formula.redundancyFactor);
  const dynPbiCost = formula.powerBiRunningCostBase + rp * formula.powerBiRunningCostPerPage;
  const dynSaving = dynExcelCost - dynPbiCost;
  const redundancyCostDyn = dynExcelCost - manualCostDyn - Math.round(errorCostDyn);

  const jsonCostRows = roiAnalysis.costComparison;
  const dynCostRows = [
    { category: jsonCostRows?.[0]?.category ?? "Manuelle Berichtspflege", excelCost: manualCostDyn, powerbiCost: 0, savings: manualCostDyn, detail: `${rp} Reportseiten × ${hpm} Std./Monat × ${hr} €/Std.` },
    { category: jsonCostRows?.[1]?.category ?? "Fehlerkorrekturen", excelCost: Math.round(errorCostDyn), powerbiCost: 0, savings: Math.round(errorCostDyn), detail: `${formula.errorRateCostPercent} % Fehlerquote auf manuelle Kosten` },
    { category: jsonCostRows?.[2]?.category ?? "Redundante Arbeit in Abteilungen", excelCost: redundancyCostDyn, powerbiCost: 0, savings: redundancyCostDyn, detail: `Redundanzfaktor ${formula.redundancyFactor}×` },
    { category: jsonCostRows?.[3]?.category ?? "Power BI Betrieb", excelCost: 0, powerbiCost: dynPbiCost, savings: -dynPbiCost, detail: `${fmtEuro(formula.powerBiRunningCostBase)} € Basis + ${rp}×${formula.powerBiRunningCostPerPage} €/Seite` },
  ];

  const scopeData = roiAnalysis.migrationScope;
  const investYear1 = (() => {
    if (!scopeData) return 120000;
    const fixPct = scopeData.phases.filter(p => scopeData.fixedPhases.includes(p.phase)).reduce((s, p) => s + p.percentage, 0);
    const scalePct = scopeData.phases.filter(p => scopeData.scalingPhases.includes(p.phase)).reduce((s, p) => s + p.percentage, 0);
    const sf = rp / scopeData.baseReportPages;
    const fixDays = Math.max(2, Math.round(scopeData.basePersonDays * fixPct / 100 * sf));
    const scaleDays = Math.round(scopeData.basePersonDays * scalePct / 100 * sf);
    return (fixDays + scaleDays) * scopeData.costPerPersonDay;
  })();
  const halfYearExcel = Math.round(dynExcelCost / 2);
  const halfYearPbi = Math.round(dynPbiCost / 2);
  const year1Balance = halfYearExcel - halfYearPbi - investYear1;
  const jsonTimeline = roiAnalysis.roiTimeline;
  const dynTimeline = [
    { year: jsonTimeline?.[0]?.year ?? "Jahr 1", description: jsonTimeline?.[0]?.description ?? "Migration (6 Monate) + Anlauf", investment: -investYear1, savings: halfYearExcel, powerbiCosts: -halfYearPbi, balance: year1Balance },
    { year: jsonTimeline?.[1]?.year ?? "Jahr 2", description: jsonTimeline?.[1]?.description ?? "Erster voller Betrieb", investment: 0, savings: dynExcelCost, powerbiCosts: -dynPbiCost, balance: year1Balance + dynSaving },
    { year: jsonTimeline?.[2]?.year ?? "Jahr 3", description: jsonTimeline?.[2]?.description ?? "Skalierung + Optimierung", investment: 0, savings: dynExcelCost, powerbiCosts: -dynPbiCost, balance: year1Balance + 2 * dynSaving },
    { year: jsonTimeline?.[3]?.year ?? "Jahr 4", description: jsonTimeline?.[3]?.description ?? "Voller ROI", investment: 0, savings: dynExcelCost, powerbiCosts: -dynPbiCost, balance: year1Balance + 3 * dynSaving },
  ];

  const totalInvestment = investYear1 + halfYearPbi + dynPbiCost * 3;
  const totalBenefit = halfYearExcel + dynExcelCost * 3;
  const roi3Year = totalInvestment > 0 ? Math.round(((totalBenefit - totalInvestment) / totalInvestment) * 100) : 0;

  const jsonHighlights = roiAnalysis.highlights;
  const dynHighlights = [
    { metric: `${fmtEuro(dynSaving)} €`, label: jsonHighlights?.[0]?.label ?? "Jährliche Netto-Einsparung", icon: jsonHighlights?.[0]?.icon ?? "TrendingDown" },
    { metric: `${roi3Year} %`, label: jsonHighlights?.[1]?.label ?? "ROI über 3 Jahre", icon: jsonHighlights?.[1]?.icon ?? "TrendingUp" },
    { metric: `${fmtEuro(dynTimeline[3].balance)} €`, label: jsonHighlights?.[2]?.label ?? "Kumulierter Gewinn nach 4 Jahren", icon: jsonHighlights?.[2]?.icon ?? "BadgeEuro" },
  ];

  const balances = dynTimeline.map((p) => p.balance);
  const barColors = balances.map((b) => b >= 0 ? "rgba(34, 197, 94, 0.85)" : "rgba(239, 68, 68, 0.85)");

  const chartJsData = {
    labels: dynTimeline.map((p) => p.year),
    datasets: [{
      label: "Bilanz kumuliert",
      data: balances,
      backgroundColor: barColors,
      borderWidth: 0,
      borderRadius: 6,
      borderSkipped: false as const,
      maxBarThickness: 72,
    }],
  };

  const chartJsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400, easing: "easeOutQuart" as const },
    layout: { padding: { top: 40, bottom: 10 } },
    scales: {
      x: {
        grid: { display: false },
        border: { color: "#d1d5db" },
        ticks: { color: "#1e3a5f", font: { weight: 600 as const, size: 13 } },
      },
      y: {
        grid: { color: "#f3f4f6", drawTicks: false },
        border: { display: false },
        ticks: {
          color: "#6b7280",
          font: { size: 12 },
          callback: (value: string | number) => `${(Number(value) / 1000).toFixed(0)}k €`,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(255,255,255,0.95)",
        titleColor: "#1e3a5f",
        bodyColor: "#374151",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        callbacks: {
          title: (items: { dataIndex: number }[]) => {
            const idx = items[0].dataIndex;
            return `${dynTimeline[idx].year} – ${dynTimeline[idx].description}`;
          },
          label: (item: { raw: unknown; dataIndex: number }) => {
            const p = dynTimeline[item.dataIndex];
            const lines = [` Bilanz kumuliert: ${formatEuro(Number(item.raw))}`];
            if (p.investment !== 0) lines.push(` Migration: ${p.investment.toLocaleString("de-DE")} €`);
            if (p.powerbiCosts !== 0) lines.push(` PBI-Betrieb: ${p.powerbiCosts.toLocaleString("de-DE")} €`);
            if (p.savings > 0) lines.push(` Einsparung: +${p.savings.toLocaleString("de-DE")} €`);
            return lines;
          },
        },
      },
    },
  };

  const roiIconMap: Record<string, React.ComponentType<{ className?: string }>> = { TrendingDown, TrendingUp, BadgeEuro };

  return (
    <div id="roi" className="mb-20 scroll-mt-24">
      <h3 className="text-2xl md:text-3xl font-bold text-abis-primary mb-4 text-center">
        {roiAnalysis.subtitle}
      </h3>
      <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-10 leading-relaxed">
        {roiAnalysis.description}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-6xl mx-auto">
        {dynHighlights.map((highlight, index) => {
          const HighlightIcon = highlight.icon && roiIconMap[highlight.icon] ? roiIconMap[highlight.icon] : TrendingUp;
          return (
            <div key={`roi-highlight-${index}`} className="bg-white rounded-xl p-6 border-2 border-abis-primary/20 shadow-lg text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 bg-gradient-to-br from-abis-primary to-abis-primary/70 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                <HighlightIcon className="w-7 h-7 text-white" />
              </div>
              <p className="text-3xl md:text-4xl font-bold text-abis-primary mb-2">{highlight.metric}</p>
              <p className="text-gray-700 font-medium">{highlight.label}</p>
            </div>
          );
        })}
      </div>

      {/* Cost Comparison Table */}
      <div className="mb-12 max-w-6xl mx-auto">
        <div className="overflow-hidden rounded-xl shadow-lg border border-gray-200">
          <table className="w-full bg-white">
            <thead>
              <tr className="bg-gradient-to-r from-abis-primary to-abis-primary/90">
                <th className="px-4 py-4 text-left text-white font-semibold text-sm">{roiAnalysis.tableHeaders?.category ?? "Kategorie"}</th>
                <th className="px-4 py-4 text-right text-white font-semibold text-sm">{roiAnalysis.tableHeaders?.excelCost ?? "Kosten Excel/Jahr"}</th>
                <th className="px-4 py-4 text-right text-white font-semibold text-sm">{roiAnalysis.tableHeaders?.powerbiCost ?? "Kosten Power BI/Jahr"}</th>
                <th className="px-4 py-4 text-right text-white font-semibold text-sm">{roiAnalysis.tableHeaders?.savings ?? "Einsparung/Jahr"}</th>
              </tr>
            </thead>
            <tbody>
              {dynCostRows.map((row, index) => (
                <tr key={`cost-row-${index}`} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                  <td className="px-4 py-4">
                    <p className="font-medium text-gray-900">{row.category}</p>
                    <p className="text-xs text-gray-500 mt-1">{row.detail}</p>
                  </td>
                  <td className={`px-4 py-4 text-right font-semibold ${row.excelCost > 0 ? "text-red-600" : "text-gray-400"}`}>
                    {fmtEuro(row.excelCost)} €
                  </td>
                  <td className={`px-4 py-4 text-right font-semibold ${row.powerbiCost > 0 ? "text-orange-600" : "text-green-600"}`}>
                    {fmtEuro(row.powerbiCost)} €
                  </td>
                  <td className={`px-4 py-4 text-right font-bold ${row.savings >= 0 ? "text-green-700" : "text-red-600"}`}>
                    {fmtEuro(row.savings)} €
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-abis-primary/10 border-t-2 border-abis-primary/30">
                <td className="px-4 py-4 font-bold text-abis-primary">{roiAnalysis.tableHeaders?.total ?? "Gesamt"}</td>
                <td className="px-4 py-4 text-right font-bold text-red-700">{fmtEuro(dynExcelCost)} €</td>
                <td className="px-4 py-4 text-right font-bold text-green-700">{fmtEuro(dynPbiCost)} €</td>
                <td className="px-4 py-4 text-right font-bold text-abis-primary text-lg">{fmtEuro(dynSaving)} €</td>
              </tr>
            </tfoot>
          </table>
        </div>
        {roiAnalysis.sources && (
          <p className="mt-3 px-1 text-[11px] text-gray-400 leading-relaxed">
            <span className="font-medium">Quellen:</span>{" "}
            {roiAnalysis.sources.map((source: string, index: number) => (
              <span key={`src-${index}`}>{source}{index < roiAnalysis.sources!.length - 1 ? " | " : ""}</span>
            ))}
          </p>
        )}
        {roiAnalysis.footnote && (
          <p className="mt-1.5 px-1 text-[11px] text-gray-400 leading-relaxed">
            <span className="font-medium">*</span>{" "}
            {roiAnalysis.footnote}
          </p>
        )}
      </div>

      {/* Interactive ROI Calculator */}
      {roiCalculatorConfig && (
        <div id="roi-calculator" className="mb-12 max-w-5xl mx-auto scroll-mt-24">
          <MigrationROICalculator config={roiCalculatorConfig} calc={roiCalc} />
        </div>
      )}

      {/* 4-Year Timeline Chart */}
      <div className="mb-12 max-w-5xl mx-auto">
        <h4 className="text-xl font-bold text-abis-primary mb-8 text-center">{roiAnalysis.chartTitle ?? "Finanzielle Entwicklung über 4 Jahre"}</h4>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
          <div className="h-[360px]">
            <ChartBar data={chartJsData} options={chartJsOptions} />
          </div>

          <div className="grid grid-cols-4 gap-0 mt-6 border-t border-gray-200 ml-[52px]">
            {dynTimeline.map((period, index) => {
              const isPositive = period.balance >= 0;
              const isLast = index === dynTimeline.length - 1;
              const netYear = period.investment + period.powerbiCosts + period.savings;
              return (
                <div key={`detail-${index}`} className={`relative pt-5 pb-4 px-3 text-center ${index < dynTimeline.length - 1 ? "border-r border-gray-200" : ""} ${isLast ? "bg-green-50/50" : ""}`}>
                  <p className={`text-2xl md:text-3xl font-extrabold tracking-tight ${isPositive ? "text-green-600" : "text-red-600"}`}>
                    {formatEuro(period.balance)}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mt-1">Bilanz kumuliert</p>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs font-semibold text-abis-primary">{period.year}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{period.description}</p>
                    <p className={`text-xs font-bold mt-2 ${netYear >= 0 ? "text-green-600" : "text-red-500"}`}>
                      {netYear >= 0 ? "+" : ""}{netYear.toLocaleString("de-DE")} € / Jahr
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Migration Scope */}
      {scopeData && <MigrationScopeSection scope={scopeData} reportPages={rp} />}
    </div>
  );
}

function MigrationScopeSection({ scope, reportPages: rp }: {
  scope: NonNullable<ROIAnalysisSectionProps["roiAnalysis"]["migrationScope"]>;
  reportPages: number;
}) {
  const basePages = scope.baseReportPages;
  const basePD = scope.basePersonDays;
  const cpd = scope.costPerPersonDay;

  const fixedPct = scope.phases.filter(p => scope.fixedPhases.includes(p.phase)).reduce((sum, p) => sum + p.percentage, 0);
  const scalingPct = scope.phases.filter(p => scope.scalingPhases.includes(p.phase)).reduce((sum, p) => sum + p.percentage, 0);
  const scaleFactor = rp / basePages;
  const fixedDays = Math.max(2, Math.round(basePD * fixedPct / 100 * scaleFactor));
  const dynScalingDays = Math.round(basePD * scalingPct / 100 * scaleFactor);
  const dynTotalDays = fixedDays + dynScalingDays;
  const dynTotalHours = dynTotalDays * 8;
  const dynInvestment = dynTotalDays * cpd;

  const sqrtPages = Math.sqrt(rp);
  const dynDeliverables = scope.deliverables.map(d => {
    if (d.label === "Reportseiten") return { ...d, value: String(rp) };
    if (d.label === "Measures & KPIs") return { ...d, value: `${Math.round(8 * sqrtPages)} – ${Math.round(16 * sqrtPages)}` };
    if (d.label === "Datenbanktabellen") return { ...d, value: `${Math.round(2 * sqrtPages)} – ${Math.round(4 * sqrtPages)}` };
    if (d.label === "Dokumentation") return { ...d, value: `${Math.max(4, Math.round(100 * scaleFactor))} Seiten` };
    return d;
  });

  const phaseColors = [
    "rgba(30, 58, 95, 0.9)",
    "rgba(30, 58, 95, 0.75)",
    "rgba(30, 58, 95, 0.6)",
    "rgba(30, 58, 95, 0.45)",
    "rgba(30, 58, 95, 0.35)",
  ];

  const phaseChartData = {
    labels: scope.phases.map((p) => `Phase ${p.phase}: ${p.title}`),
    datasets: [{
      label: "Aufwandsanteil",
      data: scope.phases.map((p) => p.percentage),
      backgroundColor: phaseColors,
      borderWidth: 0,
      borderRadius: 4,
      maxBarThickness: 32,
    }],
  };

  const phaseChartOptions = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400, easing: "easeOutQuart" as const },
    layout: { padding: { right: 50 } },
    scales: {
      x: {
        max: 55,
        grid: { display: false },
        border: { display: false },
        ticks: { display: false },
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: "#1e3a5f",
          font: { weight: 600 as const, size: 12 },
          crossAlign: "far" as const,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(255,255,255,0.95)",
        titleColor: "#1e3a5f",
        bodyColor: "#374151",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        callbacks: {
          label: (item: { raw: unknown }) => ` ${item.raw} % des Gesamtaufwands`,
        },
      },
    },
  };

  return (
    <div className="mb-12 max-w-5xl mx-auto">
      <h4 className="text-xl font-bold text-abis-primary mb-2 text-center">{scope.title}</h4>
      <p className="text-sm text-gray-500 text-center mb-2" dangerouslySetInnerHTML={{ __html: scope.subtitleTemplate.replace("{reportPages}", `<strong>${rp}</strong>`) }} />
      <div className="flex items-center justify-center mb-8">
        <p className="text-sm font-semibold text-abis-accent">{scope.uspText}</p>
      </div>
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-center gap-6 mb-8">
          <div className="flex items-center gap-2 px-4 py-2 bg-abis-primary/5 rounded-lg">
            <Settings className="w-4 h-4 text-abis-primary" />
            <span className="text-sm font-semibold text-abis-primary">{scope.effortLabel.replace("{days}", String(dynTotalDays)).replace("{hours}", dynTotalHours.toLocaleString("de-DE"))}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-abis-primary/5 rounded-lg">
            <BadgeEuro className="w-4 h-4 text-abis-primary" />
            <span className="text-sm font-semibold text-abis-primary">{scope.investmentLabel.replace("{amount}", dynInvestment.toLocaleString("de-DE"))}</span>
          </div>
        </div>

        <div className="h-[280px]">
          <ChartBar data={phaseChartData} options={phaseChartOptions} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 mt-4 border-t border-gray-100">
          {dynDeliverables.map((d, index) => (
            <div key={`del-${index}`} className="text-center p-3 bg-gray-50 rounded-xl">
              <p className="text-lg font-bold text-abis-primary">{d.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{d.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export { MigrationROICalculator };
